/**
 * The Setting line of a Trace card, one template per kind of decision (screens §2.4, lever-statements): a hire reads "Hires 10% more relationship managers than the team that keeps pace with demand growth",
 * a rate notch "+25bp on new money market balances ($10.9B)", a buyback "Buys back up to $150M a quarter while CET1 is above 10.50%", an expense level "Cuts run-rate costs 6.0%…".
 * Every number is the trace's own setting (from → to, base balance); nothing is recomputed beyond one difference. A decision no template names, or whose settings do not carry the
 * numbers it needs, falls back to the generic "+25bp rate paid … ($X)" line.
 */
import type { PreviewTrace } from "@bsim/domain";
import { formatBp, formatRate, formatUsdB, formatUsdScaled } from "../format";
import { listOf, settingText } from "./preview/traceText";

type Row = PreviewTrace["decisions"][number];
type Setting = Row["settings"][number];
interface Built { readonly sentence: string; readonly used: readonly Setting[] }
type Template = (row: Row, productName: (id: string) => string) => Built | null;

/** A setting value as a number: the trace sends a field it has no unit for as text. */
const num = (v: number | string): number | undefined => (typeof v === "number" ? v : v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : undefined);
const find = (row: Row, label: RegExp): Setting | undefined => row.settings.find((s) => label.test(s.label));
const change = (s: Setting): number | undefined => { const [a, b] = [num(s.from), num(s.to)]; return a === undefined || b === undefined ? undefined : b - a; };
const share = (v: number, precision: number): string => formatRate(Math.abs(v), precision);
const base = (s: Setting): string => (s.baseUsd === undefined ? "" : ` (${formatUsdB(s.baseUsd)})`);
const where = (s: Setting, productName: (id: string) => string): string => listOf(s.scope.map(productName));

/** Hiring: the net team change against the trend team, a share of the headcount the book already has; ramp-up is not in the trace, so it is not quoted. */
const hire: Template = (row) => {
  const s = find(row, /hiring/i), d = s === undefined ? undefined : change(s);
  if (s === undefined || d === undefined) return null;
  const who = /\.resi\./.test(row.decisionId) ? "loan officers" : "relationship managers";
  return { sentence: d < 0 ? `Cuts the number of ${who} by ${share(d, 0)} against the team that keeps pace with demand growth, so its book and deposits run off` : `Hires ${share(d, 0)} more ${who} than the team that keeps pace with demand growth, ramping up over the first months`, used: [s] };
};

/** Marketing spend a year, against what the plan would spend. */
const marketing: Template = (row) => {
  const s = find(row, /marketing/i), d = s === undefined ? undefined : change(s);
  return s === undefined || d === undefined ? null : { sentence: `${d < 0 ? "Cuts" : "Adds"} ${formatUsdScaled(Math.abs(d))} a year of marketing`, used: [s] };
};

/** A rate or spread in basis points, worded around the products it applies to: "+25bp on new money market balances ($10.9B)", "+10bp spread on new CRE loans ($8.0B)". */
const bp = (words: (id: string) => readonly [before: string, after: string]): Template => (row, productName) => {
  const s = row.settings.find((x) => x.unit === "bp" || /bp\b/i.test(x.label)), d = s === undefined ? undefined : change(s);
  if (s === undefined || d === undefined) return null;
  const [before, after] = words(row.decisionId);
  return { sentence: [formatBp(d, { signed: true }), before, where(s, productName) || row.label.toLowerCase(), after].filter((w) => w !== "").join(" ") + base(s), used: [s] };
};
const depositRate = bp((id) => (/\.ecr$/.test(id) ? ["earnings credit on", ""] : [/\.back$/.test(id) ? "on existing" : "on new", "balances"]));

const underwriting: Template = (row, productName) => {
  const s = find(row, /approval|standard|box/i), d = s === undefined ? undefined : change(s);
  return s === undefined || d === undefined ? null : { sentence: `${d > 0 ? "Loosens" : "Tightens"} approval standards on new ${where(s, productName)} loans, from ${row.fromLabel} to ${row.toLabel}${base(s)}`.replace(/new {2}loans/, "new loans"), used: [s] };
};

/** The buyback: its quarterly cap while capital stays above its target; "Off" stops it. */
const buyback: Template = (row) => {
  const target = find(row, /capital level/i), cap = find(row, /a quarter/i), to = target === undefined ? undefined : num(target.to), capUsd = cap === undefined ? undefined : num(cap.to);
  if (/^off$/i.test(row.toLabel)) return { sentence: "Stops buying back shares", used: row.settings };
  return target === undefined || to === undefined || capUsd === undefined || cap === undefined
    ? null : { sentence: `Buys back up to ${formatUsdScaled(capUsd)} a quarter while CET1 is above ${share(to, 2)}`, used: [target, cap] };
};

/** An expense level: the run-rate change, the staff it takes with it and the one-off charge it books. */
const expense: Template = (row) => {
  const cost = find(row, /controllable cost/i), staff = find(row, /staff/i), charge = find(row, /one-off/i);
  const d = cost === undefined ? undefined : change(cost);
  if (cost === undefined || d === undefined) return null;
  const people = staff === undefined ? undefined : change(staff);
  const parts = [`${d < 0 ? "Cuts" : "Raises"} run-rate costs ${share(d, 1)}`];
  if (people !== undefined && people !== 0) parts.push(`${people < 0 ? "shrinking" : "growing"} the teams by ${Math.abs(people)} staff`);
  const used = [cost, ...(people === undefined || staff === undefined ? [] : [staff]), ...(charge === undefined ? [] : [charge])];
  const one = charge === undefined ? undefined : num(charge.to);
  return { sentence: `${parts.join(", ")}${one === undefined || one === 0 ? "" : `, with a one-off charge of ${formatUsdScaled(one)}`}`, used };
};

/** Which template reads which decision, by the shape of its id (family, then the tail), not by name. */
const TEMPLATES: readonly { readonly applies: RegExp; readonly template: Template }[] = [
  { applies: /^loans\.(ci|cre|resi)\.growth$/, template: hire },
  { applies: /^loans\.consumer\.growth$|\.marketing$/, template: marketing },
  { applies: /^loans\.\w+\.pricing$/, template: bp(() => ["spread on new", "loans"]) },
  { applies: /^loans\.\w+\.underwriting$/, template: underwriting },
  { applies: /^deposits\..*(\.rate|\.ecr)/, template: depositRate },
  { applies: /^capital\.buyback/, template: buyback },
  { applies: /^operations\.expense/, template: expense },
];

/** The sentences of a card's Setting line: the template's, then the generic line for every setting it did not use. */
export function settingSentences(row: Row, productName: (id: string) => string): string[] {
  const built = TEMPLATES.find((t) => t.applies.test(row.decisionId))?.template(row, productName) ?? null;
  const rest = row.settings.filter((s) => built === null || !built.used.includes(s));
  return [...(built === null ? [] : [built.sentence]), ...rest.map((s) => settingText(s, productName))];
}
